import { FC, useMemo, useEffect } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';

interface AdverseDebtChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<{
    openModal?: () => void;
    customMessageOverlay?: React.ReactNode;
  }>;
  monthsToShow?: number;
}

// Helper function to calculate debt-to-income ratio
const calculateDTI = (adverseDebt: number, monthlyIncome: number): number => {
  if (!monthlyIncome) return 0;
  return (adverseDebt / (monthlyIncome * 12)) * 100; // Using annual income for ratio
};

// Helper function to determine risk level
const getRiskLevel = (currentDebt: number, previousDebt: number, dti: number) => {
  if (currentDebt === 0)
    return {
      icon: '✅',
      label: 'No Adverse Debt',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    };

  if (dti > 20)
    return {
      icon: '🚨',
      label: 'High Risk',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    };

  if (currentDebt > previousDebt)
    return {
      icon: '⚠️',
      label: 'Increasing',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    };

  return {
    icon: '📉',
    label: 'Decreasing',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  };
};

export const AdverseDebtChart: FC<AdverseDebtChartProps> = ({ 
  data, 
  showDebug,
  onExpandClick,
  monthsToShow = 12
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => `$${(value / 1000).toFixed(0)}k`;
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Outstanding Debt'];
  
  // Check if there has been zero debt for the last 24 months
  const hasNoDebtIn24Months = useMemo(() => {
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the last 24 months of data (or all if less than 24 months)
    const last24Months = sortedData.slice(-24);
    
    // Check if all months have zero debt
    return last24Months.every(month => (month.total_outstanding_adverse_debt || 0) === 0);
  }, [data]);
  
  // Message to display when there is no debt
  const celebrationMessage = useMemo(() => {
    if (!hasNoDebtIn24Months) return null;
    
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-white/90 rounded-lg">
        <div className="text-4xl mb-3">🏆</div>
        <h3 className="text-xl font-bold text-emerald-600 mb-2">Debt-Free Achievement!</h3>
        <p className="text-sm text-slate-600 max-w-xs">
          The member has maintained zero outstanding debt for the last 24 months.
        </p>
      </div>
    );
  }, [hasNoDebtIn24Months]);
  
  // Calculate the max value for the y-axis (0 to 20% above the largest value)
  const calculateMaxYAxis = () => {
    if (!data || data.length === 0) return 10000; // Default value if no data
    
    // Find the maximum total_outstanding_adverse_debt value in the dataset
    const maxValue = Math.max(...data.map(d => d.total_outstanding_adverse_debt || 0));
    
    // Add 20% buffer to the max value and round to a nice number for the scale
    const withBuffer = maxValue * 1.2;
    
    // Round to a nice number based on the magnitude of the value
    const magnitude = Math.pow(10, Math.floor(Math.log10(withBuffer)));
    return Math.ceil(withBuffer / magnitude) * magnitude;
  };
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    // Calculate metrics for the overlay
    const currentDebt = visibleData[visibleData.length - 1]?.total_outstanding_adverse_debt || 0;
    const previousDebt = visibleData[visibleData.length - 2]?.total_outstanding_adverse_debt || 0;
    const monthlyChange = currentDebt - previousDebt;
    const currentIncome = visibleData[visibleData.length - 1]?.gross_income || 0;
    const dti = calculateDTI(currentDebt, currentIncome);
    const riskLevel = getRiskLevel(currentDebt, previousDebt, dti);

    return (
      <div
        className="absolute left-16 top-2 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-lg border border-slate-200 shadow-sm"
        style={{ zIndex: 1 }}
      >
        <div className="grid gap-4">
          {/* Current Debt */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Outstanding Debt</div>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-brand-blue">
                ${currentDebt.toLocaleString()}
              </div>
              <div
                className={`text-sm font-medium px-2 py-1 rounded-full ${riskLevel.color} ${riskLevel.bgColor}`}
              >
                {dti.toFixed(1)}% of Income
              </div>
            </div>
          </div>

          {/* Monthly Change & Risk Level */}
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
            <div>
              <div className="text-xs text-slate-500">Monthly Change</div>
              <div className="flex items-center gap-2">
                <div
                  className="text-2xl font-semibold"
                  style={{ color: monthlyChange <= 0 ? '#10B981' : '#EF4444' }}
                >
                  {monthlyChange <= 0 ? '↓' : '↑'}
                </div>
                <div className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString()}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-500">Risk Level</div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{riskLevel.icon}</span>
                <span className={`text-sm font-medium ${riskLevel.color}`}>
                  {riskLevel.label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // useEffect to pass the celebrationMessage to the modal through the onExpandClick reference
  useEffect(() => {
    if (onExpandClick && onExpandClick.current) {
      // Attach the message to the ref so it can be accessed by ChartExpandModal
      onExpandClick.current.customMessageOverlay = hasNoDebtIn24Months ? celebrationMessage : null;
    }
  }, [onExpandClick, hasNoDebtIn24Months, celebrationMessage]);

  // Calculate max Y value
  const yAxisMax = calculateMaxYAxis();

  return (
    <BaseChartTemplate
      data={data}
      showDebug={showDebug}
      monthsToShow={monthsToShow}
      onExpandClick={onExpandClick}
      title="Outstanding Debt"
      dataKey="total_outstanding_adverse_debt"
      color="#1C4583"
      yAxisFormatter={formatCurrency}
      tooltipFormatter={formatTooltip}
      renderOverlay={renderOverlay}
      isCurrentFinances={true}
      min={0}
      max={yAxisMax}
      customMessageOverlay={celebrationMessage}
    />
  );
};
